import {
  format,
  getTime,
  differenceInDays,
  formatDistanceToNow,
  startOfWeek as dStartOfWeek,
  startOfMonth as dStartOfMonth,
  startOfYear as dStartOfYear,
  endOfWeek as dEndOfWeek,
  endOfMonth as dEndOfMonth,
  endOfYear as dEndOfYear,
  intervalToDuration,
  formatDuration,
  Duration,
  differenceInSeconds as dDifferenceInSeconds,
  differenceInHours,
  parse,
  parseISO,
  startOfDay,
  isWithinInterval,
  differenceInCalendarDays,
  addDays,
  addMonths as dAddMonths,
  subMonths as dSubMonths,
  toDate,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import i18n from "i18next";
import { DMath } from "./DMath";
import { getDateFnsLocale } from "./dateLocale";

/*
 * Two kinds of dates come from the API:
 *  - a calendar day ("2026-10-12": a bill's, expense's or due date) - shown
 *    exactly as it is. new Date("2026-10-12") is midnight UTC, which is the
 *    11th anywhere behind UTC, so a day is never read that way here;
 *  - a moment (createdAt, billedAt, ...) - shown in the branch's time zone,
 *    so everyone sees the same day and time whatever their device's zone.
 * "Today" is the branch's today. The branch's zone is set by App.tsx from
 * the current branch (setBusinessTimezone).
 */
let businessTimezone: string | null = null;

const isTimezone = (tz: any): tz is string => {
  if (typeof tz !== "string" || tz.length < 1) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (ex) {
    return false;
  }
};

/** The branch's time zone (from its settings), used for every date shown. */
export const setBusinessTimezone = (tz?: string | null) => {
  businessTimezone = isTimezone(tz) ? tz : null;
};

/** The branch's time zone, or the device's when the branch has none. */
export const getBusinessTimezone = () => businessTimezone || getUserTimezone();

/**
 * A time entered on a screen (a wall-clock Date from a picker, or a moment
 * from the API) as "2026-10-12T09:00:00" with no zone. The API reads it in
 * the branch's time zone, so 09:00 is 09:00 at the branch even when it is
 * entered from a device in another country.
 */
export const toWallClock = (value: any): string =>
  value ? format(asDate(value), "yyyy-MM-dd'T'HH:mm:ss") : value;

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** True for a plain calendar day ("2026-10-12"). */
export const isDay = (value: any): value is string =>
  typeof value === "string" && DAY_PATTERN.test(value.trim());

/**
 * A value as a Date to format or calculate with:
 *  - a calendar day ("2026-10-12") is midnight of that day here, never shifted;
 *  - a moment from the API (an ISO string or a timestamp) is the branch's
 *    wall clock;
 *  - a Date is taken as it is: the screens build them as local wall-clock
 *    values (a date picker's day, businessNow()), so converting would move them.
 */
export const asDate = (value: any): Date => {
  if (isDay(value)) return parseISO(value.trim());
  if (value instanceof Date) return value;
  const date = new Date(value);
  return businessTimezone && !isNaN(date.getTime())
    ? toZonedTime(date, businessTimezone)
    : date;
};

/** Now, as the branch's wall clock. */
export const businessNow = (): Date =>
  businessTimezone ? toZonedTime(new Date(), businessTimezone) : new Date();

/** A calendar day for a date picker (midnight of that day here). */
export const toPickerDate = (value: any): Date | null =>
  value ? (isDay(value) ? parseISO(value.trim()) : new Date(value)) : null;

/**
 * Formats for the screen in the app's language: month and day names and
 * AM/PM come from its date-fns locale ("12 Oct 2026", "12 अक्टू॰ 2026").
 * English keeps date-fns' default, so English output is unchanged.
 */
const fmt = (date: Date | number, fm: string) => {
  const lang = i18n.language || "en";
  return lang.startsWith("en")
    ? format(date, fm)
    : format(date, fm, { locale: getDateFnsLocale(lang) });
};

/**
 * Today and now for a printed bill or KOT, always in English: thermal
 * printers cannot print most scripts, so these never follow the language.
 */
export const printDate = (fm: any = "dd MMM yyyy") =>
  format(businessNow(), fm);
export const printTime = (fm: any = "h:mm:ss a") => format(businessNow(), fm);

export const currentDate = (fm: any = "dd MMM yyyy") => {
  try {
    return fmt(businessNow(), fm);
  } catch (ex) {
    return fmt(businessNow(), "dd MMM yyyy");
  }
};

export const currentTime = (fm: any = "h:mm:ss a") => {
  try {
    return fmt(businessNow(), fm);
  } catch (ex) {
    return fmt(businessNow(), "h:mm:ss a");
  }
};

export const currentDateTime = (fm: any = "dd MMM yyyy h:mm:ss a") => {
  try {
    return fmt(businessNow(), fm);
  } catch (ex) {
    return fmt(businessNow(), "dd MMM yyyy h:mm:ss a");
  }
};

export const fDate = (date: any, fm: any = "dd MMM yyyy") => {
  try {
    return date ? fmt(asDate(date), fm) : "";
  } catch (ex) {}
  return date ? fmt(asDate(date), "dd MMM yyyy") : "";
};

/**
 * A day on a chart's datetime axis. The chart reads a label "2026-10-12" as
 * midnight UTC and passes that instant here, so the day is read back in UTC
 * - never in the device's zone, where it would be the 11th behind UTC.
 */
export const fChartDay = (value: any, fm: any = "dd MMM yyyy") => {
  const date = new Date(value);
  return isNaN(date.getTime())
    ? ""
    : fDate(date.toISOString().slice(0, 10), fm);
};

export const fDateTime =(date: any, fm: any = "dd MMM yyyy h:mm:ss a") => {
  try {
    return date ? fmt(asDate(date), fm) : "";
  } catch (ex) {
    return date ? fmt(asDate(date), "dd MMM yyyy h:mm:ss a") : "";
  }
};

export const fTime = (date: any, fm: any = "h:mm:ss a") => {
  try {
    return date ? fmt(asDate(date), fm) : "";
  } catch (ex) {
    return date ? fmt(asDate(date), "h:mm:ss a") : "";
  }
};

export const fTimestamp = (date: any) => {
  return date ? getTime(new Date(date)) : "";
};

export const fTimeString = (time: any, fm: any = "h:mm a") => {
  const date = parse(time, "HH:mm", new Date());
  try {
    return fmt(date, fm);
  } catch (ex) {
    return fmt(date, "h:mm a");
  }
};

export const parseDate = (input: string | null) => {
  if (input) {
    try {
      // return parse(input, "dd/MM/yyyy hh:mm:ss a", new Date());
      return new Date(input);
    } catch (ex) {
      return null;
    }
  } else {
    return null;
  }
};

// True when the browser locale writes the day before the month (en-IN: 20/01/2026),
// false for month-first locales (en-US: 01/20/2026).
const isDayFirstLocale = () => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).formatToParts(new Date(2026, 0, 20));
    const dayIdx = parts.findIndex((p) => p.type === "day");
    const monthIdx = parts.findIndex((p) => p.type === "month");
    return dayIdx < monthIdx;
  } catch (ex) {
    return true;
  }
};

const toDateOnlyString = (y: number, m: number, d: number) => {
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return format(date, "yyyy-MM-dd");
};

const DMY_REGEX = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?:[T\s].*)?$/;

// Detects the day/month order used by a column of imported dates. A file uses one
// format for the whole column, so any value with a part > 12 tells the order
// (e.g. 18-01-2023 => day first). Returns undefined if every value is ambiguous.
export const detectDayFirst = (values: any[]): boolean | undefined => {
  let dayFirst = 0;
  let monthFirst = 0;
  values.forEach((v) => {
    if (typeof v !== "string") return;
    const match = v.trim().match(DMY_REGEX);
    if (!match) return;
    const a = +match[1];
    const b = +match[2];
    if (a > 12 && b <= 12) dayFirst++;
    else if (b > 12 && a <= 12) monthFirst++;
  });
  if (dayFirst === 0 && monthFirst === 0) return undefined;
  return dayFirst >= monthFirst;
};

// Normalizes a date coming from an imported spreadsheet / CSV (any locale format,
// Date object or Excel serial number) into "yyyy-MM-dd". Returns null if invalid.
// Ambiguous values like 05/06/2026 are resolved with `dayFirst` (use
// detectDayFirst on the whole column), falling back to the browser locale order.
export const toImportDate = (
  input: any,
  dayFirst?: boolean,
): string | null => {
  if (input === null || input === undefined || input === "") return null;

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : format(input, "yyyy-MM-dd");
  }

  const value = String(input).trim();

  // Excel serial date (days since 1899-12-30)
  if (/^\d{5}(\.\d+)?$/.test(value)) {
    const serial = Math.floor(Number(value));
    const date = new Date(1899, 11, 30 + serial);
    return format(date, "yyyy-MM-dd");
  }

  // yyyy-MM-dd, yyyy/MM/dd, yyyy.MM.dd (optionally followed by time)
  let match = value.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/);
  if (match) {
    return toDateOnlyString(+match[1], +match[2], +match[3]);
  }

  // dd/MM/yyyy or MM/dd/yyyy (also - or . separators, 2 digit years, optional time)
  match = value.match(DMY_REGEX);
  if (match) {
    const a = +match[1];
    const b = +match[2];
    let y = +match[3];
    if (match[3].length === 2) y += 2000;
    const isDayFirst = dayFirst ?? isDayFirstLocale();
    let d = isDayFirst ? a : b;
    let m = isDayFirst ? b : a;
    if (a > 12 && b <= 12) {
      d = a;
      m = b;
    } else if (b > 12 && a <= 12) {
      d = b;
      m = a;
    }
    return toDateOnlyString(y, m, d);
  }

  // Textual formats like "20 Jan 2026" or "Jan 20, 2026"
  if (/[a-z]/i.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) return format(date, "yyyy-MM-dd");
  }

  return null;
};

export const toUTC = (date: string | Date, tz?: string): Date => {
  if (tz) {
    return fromZonedTime(date, tz);
  }
  if (typeof date === "string") {
    return parseISO(date);
  }
  return date;
};

export const fToNow = (date: Date | string | undefined): string => {
  if (!date) return "";

  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    locale: {
      formatDistance: (token: string, count: number) => {
        // return i18n.t(token, { count });
        return i18n.t(`dateDistance.${token}`, { count });
      },
    },
  });
};

export const fFromToDuration = (
  from: Date | string | undefined,
  to: Date | string | undefined,
): string => {
  if (!from || !to) return "";

  const duration = intervalToDuration({
    start: new Date(from),
    end: new Date(to),
  });

  return formatDuration(duration, {
    format: ["years", "months", "days", "hours", "minutes", "seconds"],
    locale: {
      formatDistance: (token: string, count: number) => {
        // return i18n.t(token, { count });
        return i18n.t(`dateDuration.${token}`, { count });
      },
    },
  });
};

export const fDistance = (date1: any, date2: any) => {
  return date1 && date2
    ? differenceInDays(new Date(date1), new Date(date2))
    : 0;
};

export const fDistanceInclusive = (date1: string, date2: string) => {
  if (!date1 || !date2) return 0;

  return differenceInCalendarDays(toUTC(date1), toUTC(date2)) + 1;
};

export const diffInHours = (date1: any, date2: any) => {
  return differenceInHours(date1, date2);
};

export const secondsToDuration = (seconds: number): Duration => {
  return milliSecondsToDuration(seconds * 1000);
};
export const milliSecondsToDuration = (milliSeconds: number): Duration => {
  const epoch = new Date(0);
  const secondsAfterEpoch = new Date(milliSeconds);
  return intervalToDuration({
    start: epoch,
    end: secondsAfterEpoch,
  });
};

export const differenceInSeconds = (start: any, end: any) => {
  return start && end
    ? dDifferenceInSeconds(new Date(end), new Date(start))
    : 0;
};

// export const secondsToHours = (input: string) => {
//   let decimalTime = DMath.toDecimal(input);

//   // let hours: number | string = Math.floor(decimalTime / (60 * 60));
//   // decimalTime = decimalTime - hours * 60 * 60;
//   // let minutes: number | string = Math.floor(decimalTime / 60);
//   // decimalTime = decimalTime - minutes * 60;
//   // let seconds: number | string = Math.round(decimalTime);

//   let hours: string = DMath.toFixed(DMath.div(decimalTime, DMath.mul(60, 60)));
//   decimalTime = DMath.sub(decimalTime, DMath.mul(hours, DMath.mul(60, 60)));
//   let minutes: string = DMath.toFixed(DMath.div(decimalTime, 60));
//   decimalTime = DMath.sub(decimalTime, DMath.mul(minutes, 60));
//   let seconds: string = DMath.toFixed(decimalTime);

//   // if (DMath.lt(hours, 10)) {
//   //   hours = `0${hours}`;
//   // }
//   // if (DMath.lt(minutes, 10)) {
//   //   minutes = `0${minutes}`;
//   // }
//   // if (DMath.lt(seconds, 10)) {
//   //   seconds = `0${seconds}`;
//   // }
//   return `${hours}:${minutes}:${seconds}`;
// };

// export const secondsToHours = (input: string) => {
//   let totalSeconds = Math.round(DMath.toNumber(input));

//   const hours = Math.floor(totalSeconds / 3600);
//   totalSeconds = totalSeconds % 3600;

//   const minutes = Math.floor(totalSeconds / 60);
//   const seconds = totalSeconds % 60;

//   const hh = hours.toString().padStart(2, "0");
//   const mm = minutes.toString().padStart(2, "0");
//   const ss = seconds.toString().padStart(2, "0");

//   return `${hh}:${mm}:${ss}`;
// };

export const secondsToHours = (input: string) => {
  let totalSeconds = Math.round(DMath.toNumber(input));

  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds = totalSeconds % 3600;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const hh = hours.toString().padStart(2, "0");
  const mm = minutes.toString().padStart(2, "0");
  const ss = seconds.toString().padStart(2, "0");

  return `${hh}:${mm}:${ss}`;
};

export const combineDateAndTime = (
  dateString: string,
  time: Date | string,
  tz?: string,
) => {
  // const timeSource = toUTC(time, tz);
  // The time of day on the branch's clock (a shift's start, say): the given
  // branch's when its zone is passed, else the current branch's.
  const timeSource =
    tz && !(time instanceof Date) && !isNaN(new Date(time).getTime())
      ? toZonedTime(new Date(time), tz)
      : asDate(time);
  // Extract time parts
  const hours = timeSource.getHours();
  const minutes = timeSource.getMinutes();
  const seconds = timeSource.getSeconds();
  const milliseconds = timeSource.getMilliseconds();

  // Create new Date using date + extracted time
  const [year, month, day] = dateString.split("-").map(Number);

  return toUTC(
    new Date(
      year,
      month - 1, // Month is 0-based in JS
      day,
      hours,
      minutes,
      seconds,
      milliseconds,
    ),
    tz,
  );
};

// export const timeToSeconds = (time: any) => {
//   const [h, m, s] = time.split(":").map(Number);
//   return h * 3600 + m * 60 + s;
// };

// export const secondsToTime = (seconds: any) => {
//   const date = addSeconds(new Date(0), seconds);
//   return format(date, "HH:mm:ss");
// };

export const startOfWeek = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dStartOfWeek(asDate(date)), fm) : "";
};
export const startOfMonth = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dStartOfMonth(asDate(date)), fm) : "";
};
export const startOfYear = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dStartOfYear(asDate(date)), fm) : "";
};
export const startOfFinYear = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dStartOfYear(asDate(date)), fm) : "";
};
export const endOfWeek = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dEndOfWeek(asDate(date)), fm) : "";
};
export const endOfMonth = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dEndOfMonth(asDate(date)), fm) : "";
};
export const endOfYear = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dEndOfYear(asDate(date)), fm) : "";
};
export const endOfFinYear = (date: any, fm: any = "yyyy-MM-dd") => {
  return date ? format(dEndOfYear(asDate(date)), fm) : "";
};

export const addMonths = (
  date: any,
  months: number,
  fm: any = "yyyy-MM-dd",
) => {
  return date ? format(dAddMonths(asDate(date), months), fm) : "";
};
export const subMonths = (
  date: any,
  months: number,
  fm: any = "yyyy-MM-dd",
) => {
  return date ? format(dSubMonths(asDate(date), months), fm) : "";
};

export const addZeroBefore = (n: number | undefined | null) => {
  return n ? `${n < 10 ? "0" : ""}${n}` : "00";
};

export function dayStart(date?: string | Date): Date {
  const baseDate = date ? asDate(date) : businessNow();
  return startOfDay(baseDate);
}

export const compareDates = (date1: any, date2: any) => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  if (d1 > d2) {
    return 1; // date1 is later
  } else if (d1 < d2) {
    return -1; // date1 is earlier
  } else {
    return 0; // dates are equal
  }
};

export const isBetween = (date: Date, from: Date, to: Date): boolean => {
  return isWithinInterval(date, {
    start: from,
    end: to,
  });
};

export const intervalsOverlap = (
  startA: Date | string | number,
  endA: Date | string | number,
  startB: Date | string | number,
  endB: Date | string | number,
): boolean => {
  let aStart = toDate(startA);
  let aEnd = toDate(endA);

  let bStart = toDate(startB);
  let bEnd = toDate(endB);

  if (aEnd <= aStart) aEnd = addDays(aEnd, 1);
  if (bEnd <= bStart) bEnd = addDays(bEnd, 1);

  return aStart < bEnd && aEnd > bStart;
};

/**
 * A date as the device writes it; with no date, the branch's today as
 * "yyyy-MM-dd" (what a date field's max / min and the date pickers expect).
 */
export const toLocaleDateString = (date?: any) => {
  if (date === undefined) return currentDate("yyyy-MM-dd");
  return date?.toLocaleDateString(undefined);
};

export const getStartDate = (
  _date: string,
  _type: string,
  fm: any = "yyyy-MM-dd",
) => {
  switch (_type) {
    case "W":
      return startOfWeek(_date, fm);
    case "M":
      return startOfMonth(_date, fm);
    case "Y":
      return startOfYear(_date, fm);
    case "A":
      return "";
    default:
      return fDate(_date, fm);
  }
};

export const getEndDate = (
  _date: string,
  _type: string,
  fm: any = "yyyy-MM-dd",
) => {
  switch (_type) {
    case "W":
      return endOfWeek(_date, fm);
    case "M":
      return endOfMonth(_date, fm);
    case "Y":
      return endOfYear(_date, fm);
    case "A":
      return "";
    default:
      return fDate(_date, fm);
  }
};

export const getUserTimezone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz.toLowerCase() === "asia/calcutta") {
    return "Asia/Kolkata";
  } else {
    return tz || "UTC";
  }
};
